import { memo, type MouseEvent, type PointerEvent, type ReactElement } from 'react';
import type { FleetAircraftView, AircraftType } from '../../../domain/models/contracts';
import {
  CHECK_RULES,
  checkStatusMessage,
  formatMinuteOfDay,
  type CheckStatus,
  type TimelineBlock,
} from '../../../domain/scheduling/scheduleMath';
import { ScheduleBlock } from './ScheduleBlock';
import { minuteToPercent, pointerToMinute } from './timelineUtils';

interface AircraftTrackProps {
  aircraft: FleetAircraftView;
  type: AircraftType | null;
  blocks: readonly TimelineBlock[];
  checkStatuses: readonly CheckStatus[];
  dailyHours: number;
  selected: boolean;
  selectedLegId: string | null;
  /** Hover position, only for the track the pointer is over. */
  hoverMinute: number | null;
  onSelectAircraft: (reg: string) => void;
  onSelectBlock: (block: TimelineBlock) => void;
  onLaneClick: (reg: string, minute: number) => void;
  onLaneHover: (reg: string, minute: number | null) => void;
}

function AircraftTrackImpl({
  aircraft,
  type,
  blocks,
  checkStatuses,
  dailyHours,
  selected,
  selectedLegId,
  hoverMinute,
  onSelectAircraft,
  onSelectBlock,
  onLaneClick,
  onLaneHover,
}: AircraftTrackProps): ReactElement {
  const reg = aircraft.registration;

  const minuteFromPointer = (event: { clientX: number; currentTarget: HTMLDivElement }): number => {
    const rect = event.currentTarget.getBoundingClientRect();
    return pointerToMinute(event.clientX, rect.left, rect.width);
  };

  const handleLaneClick = (event: MouseEvent<HTMLDivElement>): void => {
    if (event.target !== event.currentTarget) return; // a block was clicked
    onLaneClick(reg, minuteFromPointer(event));
  };

  const handleLaneMove = (event: PointerEvent<HTMLDivElement>): void => {
    if (event.target !== event.currentTarget) {
      onLaneHover(reg, null);
      return;
    }
    onLaneHover(reg, minuteFromPointer(event));
  };

  const warnings = checkStatuses.filter((status) => status.severity !== 'OK');

  return (
    <div className={selected ? 'fsg-row fsg-track is-selected' : 'fsg-row fsg-track'} role="row">
      <div className="fsg-label" role="rowheader">
        <button
          type="button"
          className="fsg-label-btn"
          onClick={() => onSelectAircraft(reg)}
          aria-pressed={selected}
        >
          <span className="fsg-label-reg">{reg}</span>
          <span className="fsg-label-type">{type === null ? `Unknown type ${aircraft.typeId}` : type.id}</span>
        </button>
        <span className="fsg-label-meta" title="Scheduled flying per day">
          {dailyHours.toFixed(1)}h/day
        </span>
        {warnings.map((status) => (
          <span
            key={status.check}
            className={`fsg-badge fsg-badge-${status.severity.toLowerCase()}`}
            title={
              status.severity === 'SCHEDULED'
                ? `${CHECK_RULES[status.check].label} scheduled`
                : checkStatusMessage(status)
            }
          >
            {status.check === 'A_CHECK' ? 'A' : 'C'}
            {status.severity === 'OVERDUE'
              ? ' overdue'
              : ` ${Math.max(0, status.hoursRemaining).toFixed(0)}h`}
          </span>
        ))}
      </div>

      <div
        className="fsg-lane"
        role="gridcell"
        aria-label={`${reg} schedule. Click an empty time window to add a flight leg.`}
        onClick={handleLaneClick}
        onPointerMove={handleLaneMove}
        onPointerLeave={() => onLaneHover(reg, null)}
      >
        {blocks.map((block) => (
          <ScheduleBlock
            key={block.key}
            block={block}
            selected={block.legId !== null && block.legId === selectedLegId}
            onSelect={onSelectBlock}
          />
        ))}
        {hoverMinute !== null && (
          <div className="fsg-ghost" style={{ left: `${minuteToPercent(hoverMinute)}%` }}>
            <span className="fsg-ghost-tag">{formatMinuteOfDay(hoverMinute)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export const AircraftTrack = memo(AircraftTrackImpl);
